import {
  CustomFieldDefinition,
  MetastructureField,
  MetastructureFieldType,
  ResourceType
} from '__types__';
import { ResourceTypeFieldAssignment } from 'v5/api/resource-types/resource-type-bindings.api';
import { mapAssignmentsToMetastructureFields } from 'v5/services/internal/ResourceTypeBindingsService/mapAssignmentsToMetastructureFields';
import {
  formatCustomFieldDataTypeLabel,
  getCoreFieldDataTypeLabel,
  getCoreFieldLabel
} from 'v5/services/internal/MetastructureMappingService/coreFieldLabels';
import { ResourceTypeAdmin, ResourceTypeFieldView } from './model';

export { mapAssignmentsToMetastructureFields };

/** Lean persistence assignments from metastructure (no display labels). */
export function mapMetastructureFieldsToAssignments(
  fields: MetastructureField[] | undefined
): ResourceTypeFieldAssignment[] {
  if (!fields?.length) return [];

  return fields
    .map((field, index): ResourceTypeFieldAssignment | null => {
      if (field.type === MetastructureFieldType.Core) {
        return {
          id: `core:${field.name}`,
          source: 'core',
          coreFieldKey: field.name,
          required: field.required ?? false,
          locked: field.locked ?? false,
          active: field.active ?? true,
          order: index
        };
      }
      if (field.type === MetastructureFieldType.Custom) {
        return {
          id: `custom:${field.custom_field_definition_id}`,
          source: 'custom',
          customFieldDefinitionId: field.custom_field_definition_id,
          required: field.required ?? false,
          locked: field.locked ?? false,
          active: field.active ?? true,
          order: index
        };
      }
      // Custom groups: skip in v2 admin UI for now (not in mockups)
      return null;
    })
    .filter((field): field is ResourceTypeFieldAssignment => field != null);
}

/**
 * Resolve display name / type label / required / filterable from definitions +
 * core labels. For custom fields, `required` and `filterable` come from the
 * custom field definition (same source forms and the CF config dialog use) —
 * not from the metastructure assignment, which often stays at the default
 * `false` when fields are assigned.
 */
export function resolveFieldAssignments(
  assignments: ResourceTypeFieldAssignment[],
  definitionsById: Record<number, CustomFieldDefinition>
): ResourceTypeFieldView[] {
  return assignments.map(assignment => {
    if (assignment.source === 'core') {
      return {
        ...assignment,
        name: getCoreFieldLabel(assignment.coreFieldKey),
        dataTypeLabel: getCoreFieldDataTypeLabel(),
        filterable: true
      };
    }
    const def = definitionsById[assignment.customFieldDefinitionId];
    return {
      ...assignment,
      name: def?.name ?? `Custom field ${assignment.customFieldDefinitionId}`,
      dataTypeLabel: formatCustomFieldDataTypeLabel(def?.data_type ?? 'CUSTOM'),
      // Prefer definition; fall back to assignment when the catalog row is missing.
      required: def?.required ?? assignment.required,
      filterable: def?.filterable ?? false
    };
  });
}

/** Drop view-only props before writing bindings / metastructure. */
export function toFieldAssignment(
  field: ResourceTypeFieldAssignment | ResourceTypeFieldView
): ResourceTypeFieldAssignment {
  if (field.source === 'core') {
    return {
      id: field.id,
      source: 'core',
      coreFieldKey: field.coreFieldKey,
      required: field.required,
      locked: field.locked,
      active: field.active,
      order: field.order
    };
  }
  return {
    id: field.id,
    source: 'custom',
    customFieldDefinitionId: field.customFieldDefinitionId,
    required: field.required,
    locked: field.locked,
    active: field.active,
    order: field.order
  };
}

export function mapResourceTypeToAdmin(
  resourceType: ResourceType,
  definitionsById: Record<number, CustomFieldDefinition> = {}
): ResourceTypeAdmin {
  return {
    id: resourceType.id,
    name: resourceType.name ?? '',
    icon: resourceType.icon ?? 'person',
    mechanism: resourceType.model ?? 'INDIVIDUAL',
    archived: Boolean(resourceType.archived),
    order: resourceType.order ?? 0,
    globalFields: resolveFieldAssignments(
      mapMetastructureFieldsToAssignments(resourceType.metastructure?.fields),
      definitionsById
    )
  };
}
